import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Card, Button, Chip, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { tokens } from "../../theme";
import { pageContainerSx } from "../../components/ui";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import {
  PRIVACY_POLICY_LOAD_ERROR_MESSAGE,
  PRIVACY_POLICY_OPEN_ERROR_MESSAGE,
} from "../../utils/errorMessages";

// Shows the PDF an administrator uploaded for the signed-in user's company. The
// endpoint takes no company id — the server resolves it from the session token,
// so a customer can only ever fetch their own.
const POLICY_URL = `${CONFIG.BASE_URL}/api/portal/profile/privacy-policy`;

/** "2.4 MB" — the raw byte count isn't meaningful to read. */
const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** "Uploaded 11 Aug 2026" — omitted when the date is missing or unparseable. */
const formatUploadedAt = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `Uploaded ${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
};

/** `sections` are extracted from the PDF server-side, so no PDF library ships in the bundle. */
const PolicySections = ({ sections }) => (
  <Box>
    {sections.map((section, i) => (
      // An extracted section is identified by its heading; the leading section of
      // a policy can be untitled, so its first block's text stands in. `i` below
      // is only a last-item test for the divider, not an identity.
      <Box
        key={section.heading || section.blocks[0]?.text || section.blocks[0]?.items?.[0]}
        sx={{
          py: 3,
          borderBottom: i === sections.length - 1 ? "none" : `1px solid ${tokens.border}`,
        }}
      >
        {section.heading && (
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: tokens.text, mb: 1.25 }}>
            {section.heading}
          </Typography>
        )}

        {/* Keys come from the block/item TEXT, not the array index: the policy is
            static once rendered, and its own content is what uniquely identifies
            a paragraph or bullet. */}
        <Stack gap={1.25}>
          {section.blocks.map((block) =>
            block.type === "bullets" ? (
              <Stack key={block.items.join("|")} gap={1}>
                {block.items.map((item) => (
                  <Stack key={item} direction="row" gap={1} alignItems="flex-start">
                    <Typography
                      sx={{ color: tokens.accent, fontSize: 14, lineHeight: 1.6, flexShrink: 0 }}
                    >
                      ›
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, lineHeight: 1.6, color: tokens.text2 }}>
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography
                key={block.text}
                sx={{ fontSize: 13.5, lineHeight: 1.7, color: tokens.text2 }}
              >
                {block.text}
              </Typography>
            )
          )}
        </Stack>
      </Box>
    ))}
  </Box>
);

PolicySections.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string,
      blocks: PropTypes.array,
    })
  ).isRequired,
};

// `embedded` renders this inside the Settings tab pane (no self-contained page
// chrome). Default/false keeps the standalone /privacy route identical.
const Privacy = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The response carries a pre-signed URL that expires, so it's fetched on mount
  // rather than cached — and re-fetched by "Open" if the user lingers.
  const fetchPolicy = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // "portalToken", not "token": that key belongs to the admin app.
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(POLICY_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 404 is the ordinary "nothing uploaded" case, not a failure to report.
      if (res.status === 404) {
        setPolicy(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Could not load the privacy policy");
      }
      setPolicy(await res.json());
    } catch {
      setError(PRIVACY_POLICY_LOAD_ERROR_MESSAGE);
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  // Re-fetch before opening: the URL held in state may have expired while the
  // tab sat open, and a stale pre-signed link fails with an S3 error page.
  const handleOpen = useCallback(async () => {
    try {
      // "portalToken", not "token": that key belongs to the admin app.
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(POLICY_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Could not open the privacy policy");
      const fresh = await res.json();
      if (!fresh?.url) throw new Error("No document link was returned");
      window.open(fresh.url, "_blank", "noopener,noreferrer");
    } catch {
      setError(PRIVACY_POLICY_OPEN_ERROR_MESSAGE);
    }
  }, []);

  return (
    <Box sx={pageContainerSx({ embedded, prose: true })}>
      {!embedded && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/settings")}
          size="small"
          sx={{
            mb: 1.5,
            color: tokens.text2,
            "&:hover": { color: tokens.text, background: tokens.surface2 },
          }}
        >
          Back to Settings
        </Button>
      )}

      <Card sx={{ overflow: "hidden" }}>
        {/* Hero header */}
        <Box
          sx={{
            textAlign: "center",
            px: 3,
            pt: 4,
            pb: 3.5,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: tokens.accentSoft,
              color: tokens.accent,
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <GppGoodOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography
            sx={{ fontSize: 24, fontWeight: 700, letterSpacing: "-.02em", color: tokens.text }}
          >
            Privacy Policy
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: tokens.text2,
              lineHeight: 1.6,
              mt: 1,
              maxWidth: 520,
              mx: "auto",
            }}
          >
            {policy?.companyName
              ? `The privacy policy published by ${policy.companyName}.`
              : "The privacy policy published by your company."}
          </Typography>
          {policy?.uploadedAt && formatUploadedAt(policy.uploadedAt) && (
            <Chip
              label={formatUploadedAt(policy.uploadedAt)}
              size="small"
              sx={{
                mt: 2,
                height: 26,
                fontSize: 11.5,
                fontWeight: 500,
                background: tokens.surface2,
                border: `1px solid ${tokens.border}`,
                color: tokens.text3,
              }}
            />
          )}
        </Box>

        {/* Body — loading / error / document / empty */}
        <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 4 }}>
          {loading && (
            <Stack alignItems="center" gap={1.5} sx={{ py: 3 }}>
              <CircularProgress size={26} sx={{ color: tokens.accent }} />
              <Typography sx={{ fontSize: 13.5, color: tokens.text3 }}>
                Loading privacy policy…
              </Typography>
            </Stack>
          )}

          {!loading && error && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: tokens.dangerSoft,
                color: tokens.danger,
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {error}
            </Box>
          )}

          {!loading && !error && policy && (
            <Stack gap={1.5}>
              {/* File name + secondary "open in a new tab" action. The button is
                  no longer the way to READ the policy (the frame below is) — it
                  remains for printing, full-screen, and as the mobile fallback. */}
              <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexWrap: "wrap" }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "9px",
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    background: tokens.accentSoft,
                    color: tokens.accent,
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 19 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: tokens.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {policy.fileName || "privacy-policy.pdf"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.text3 }}>
                    {formatFileSize(policy.fileSize)}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpen}
                  sx={{
                    flexShrink: 0,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    color: tokens.text2,
                    borderColor: tokens.border,
                    "&:hover": { borderColor: tokens.accent, color: tokens.accent },
                  }}
                >
                  Open in new tab
                </Button>
              </Stack>

              {/* Preferred path: the policy as real text, styled like the rest of
                  the app — selectable, searchable, theme-aware, and readable on a
                  phone. The PDF stays one click away via the button above. */}
              {policy.extracted && policy.sections?.length > 0 ? (
                <PolicySections sections={policy.sections} />
              ) : (
                <>
                  {/* Fallback when the PDF had no extractable text. Hidden below `sm`:
                      iOS Safari and some Android browsers render a blank frame. */}
                  <Box
                    sx={{
                      display: { xs: "none", sm: "block" },
                      height: "72vh",
                      minHeight: 460,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: `1px solid ${tokens.border}`,
                      background: tokens.surface2,
                    }}
                  >
                    <Box
                      component="iframe"
                      src={`${policy.url}#view=FitH`}
                      title={policy.fileName || "Privacy policy"}
                      sx={{ width: "100%", height: "100%", border: "none", display: "block" }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      display: { xs: "block", sm: "none" },
                      fontSize: 13,
                      color: tokens.text2,
                      textAlign: "center",
                      lineHeight: 1.6,
                      py: 1,
                    }}
                  >
                    Open the document above to read your company&apos;s privacy policy.
                  </Typography>
                </>
              )}
            </Stack>
          )}

          {!loading && !error && !policy && (
            <Stack alignItems="center" gap={1.25} sx={{ py: 3, textAlign: "center" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: tokens.surface2,
                  color: tokens.text3,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 24 }} />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: tokens.text }}>
                No privacy policy available
              </Typography>
              <Typography
                sx={{ fontSize: 13.5, color: tokens.text2, maxWidth: 420, lineHeight: 1.6 }}
              >
                Your company has not published a privacy policy yet. Please contact your
                administrator if you need a copy.
              </Typography>
            </Stack>
          )}
        </Box>
      </Card>
    </Box>
  );
};

Privacy.propTypes = {
  embedded: PropTypes.bool,
};

export default Privacy;
